import React, { useEffect, useRef, useState } from 'react';
import { GenericSearchableSelect, type SearchableSelectOption } from '@/styles/GenericSearchableSelect';
import { SmallStandardButton, SmallSecondaryButton } from '@/styles/StandardButtons';
import { NO_INTERNAL_MEMBER_MESSAGE, searchProjectPeople, type ProjectPeoplePage, type ProjectPerson } from './projectAccessApi';
import type { ProjectMembersState } from './useProjectMembers';

const ROLE_LABEL: Record<ProjectPerson['company_role'], string> = { admin: 'Internal Member', member: 'Internal Member', client: 'Client', vendor: 'Vendor' };
const groupOf = (p: ProjectPerson) => (p.kind === 'internal' ? 'internal' : p.company_role);
const SELECT_ALL = '__select_all_internal__';
const SEARCH_DEBOUNCE_MS = 250;

/**
 * One dropdown for both tables. It shows a first page of the people who can be added (internal team, clients and vendors, each labelled with what they are) as soon as it opens, and
 * what is typed is searched on the server over the whole company (debounced), a page at a time ("Show more"). A choice goes straight into its own table. Nothing is stored: it only
 * changes the draft that the modal's Save Changes persists, and the draft is what decides who is not offered again.
 */
function MemberPicker({ projectId, taken, onAdd }: { projectId: string; taken: string[]; onAdd: (people: ProjectPerson[]) => void }) {
  const [query, setQuery] = useState('');
  const [people, setPeople] = useState<ProjectPerson[]>([]);
  const [next, setNext] = useState<string | null>(null);
  const request = useRef(0);
  const draft = [...taken].sort().join(',');

  // a new page 1 when the text typed or the draft changes (an answer to an older request is dropped)
  useEffect(() => {
    const mine = ++request.current;
    const timer = setTimeout(() => {
      searchProjectPeople(projectId, { q: query, exclude: draft ? draft.split(',') : [] })
        .then((page) => { if (mine === request.current) { setPeople(page.results); setNext(page.next); } })
        .catch(() => { if (mine === request.current) { setPeople([]); setNext(null); } });
    }, query ? SEARCH_DEBOUNCE_MS : 0);
    return () => clearTimeout(timer);
  }, [projectId, query, draft]);

  const showMore = () => {
    if (!next) return;
    const mine = request.current;
    searchProjectPeople(projectId, { q: query, cursor: next, exclude: draft ? draft.split(',') : [] })
      .then((page) => {
        if (mine !== request.current) return;
        setPeople((current) => [...current, ...page.results.filter((p) => !current.some((c) => c.local_id === p.local_id))]);
        setNext(page.next);
      })
      .catch(() => { if (mine === request.current) setNext(null); });
  };

  // "Select all" is every internal person who is not in the draft, not just the page on screen
  const selectAllInternal = async () => {
    const everyone: ProjectPerson[] = [];
    let cursor: string | null = null;
    do {
      const page: ProjectPeoplePage = await searchProjectPeople(projectId, { kind: 'internal', cursor, exclude: taken });
      everyone.push(...page.results);
      cursor = page.next;
    } while (cursor);
    onAdd(everyone);
  };

  const available = people.filter((p) => !taken.includes(p.local_id));
  const offersSelectAll = !query && (next !== null || available.filter((p) => p.kind === 'internal').length > 1);
  const options: SearchableSelectOption[] = [
    ...(offersSelectAll ? [{ value: SELECT_ALL, label: 'Select all internal members' }] : []),
    // the server sends the internal team, then vendors, then clients: a thin line starts each group after the first
    ...available.map((p, i) => ({ value: p.local_id, label: `${p.name} · ${ROLE_LABEL[p.company_role]}`, dividerBefore: i > 0 && groupOf(available[i - 1]) !== groupOf(p) })),
  ];
  const choose = (value: string) => {
    if (value === SELECT_ALL) void selectAllInternal();
    else { const chosen = available.find((p) => p.local_id === value); if (chosen) onAdd([chosen]); }
  };

  return (
    <div className="max-w-sm">
      <GenericSearchableSelect
        value="" onValueChange={choose} options={options} placeholder="Search by name or email" ariaLabel="Add members" emptyText="No one to add"
        onSearchChange={setQuery} onLoadMore={showMore} hasMore={next !== null}
      />
    </div>
  );
}

function MemberTable({ title, rows, empty, onRemove, lockedId, removable = () => true }: {
  title: string;
  rows: ProjectPerson[];
  empty: string;
  onRemove: (localId: string) => void;
  /** the person who cannot be removed because the project would be left without an internal member */
  lockedId?: string | null;
  /** false: the person stays (somebody who can only add people cannot take a stored member out) */
  removable?: (localId: string) => boolean;
}) {
  return (
    <div role="group" aria-label={title} className="rounded-md border border-border p-1">
      <p className="px-2 py-1 text-xs font-medium text-gray-600">{title}</p>
      {rows.length === 0 && <p className="px-2 py-1.5 text-sm text-gray-500">{empty}</p>}
      {rows.map((person) => (
        <div key={person.local_id} className="flex items-center gap-2 rounded px-2 py-1.5 text-sm">
          <span className="min-w-0 flex-1 truncate">
            <span className="font-medium">{person.name}</span>
            <span className="ml-2 text-xs text-gray-500">{person.company_role}</span>
          </span>
          {removable(person.local_id) && (
            <SmallStandardButton type="button" customVariant="white" disabled={person.local_id === lockedId} title={person.local_id === lockedId ? NO_INTERNAL_MEMBER_MESSAGE : undefined} onClick={() => onRemove(person.local_id)}>
              Remove
            </SmallStandardButton>
          )}
        </div>
      ))}
    </div>
  );
}

/** Edit Project > Members: Internal Members and Clients / Vendors, with one picker for both. Admins and members of the project add and remove anybody. Edits stay in the modal's draft until Save Changes. */
export function ProjectMembersTab({ projectId, members }: { projectId: string; members: ProjectMembersState }) {
  if (members.loading) return <p className="text-sm text-gray-500">Loading members...</p>;
  if (members.loadError || !members.loaded) {
    return (
      <div className="space-y-2">
        <div className="generic-form-error" role="alert">{members.loadError || 'Project members could not be loaded'}</div>
        <SmallSecondaryButton type="button" onClick={() => void members.load()}>Retry</SmallSecondaryButton>
      </div>
    );
  }
  const lockedId = members.internal.length === 1 ? members.internal[0].local_id : null;
  return (
    <div className="space-y-4" data-testid="project-members">
      <MemberPicker projectId={projectId} taken={members.ids} onAdd={members.add} />
      <MemberTable title="Internal Members" rows={members.internal} empty="No internal members." onRemove={members.remove} lockedId={lockedId} />
      <MemberTable title="Clients / Vendors" rows={members.external} empty="No clients or vendors in this project." onRemove={members.remove} />
    </div>
  );
}
